import React, { useState, useEffect, useMemo } from 'react'
import {
  Card, CardContent, Typography, TextField, Select, MenuItem,
  FormControl, InputLabel, FormControlLabel, Checkbox, Button, Grid
} from '@material-ui/core'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import type { EmbedTheme } from '../../embed/types'
import PartnerMultiSelect, { PartnerOption } from './partials/PartnerMultiSelect'

export type ContentType = 'conversations' | 'topics'
type CodeFormat = 'iframe' | 'script'

/**
 * "Partner" used to be a third content type here, which was a category error:
 * a partner is not a kind of content, it is a filter over content. It rendered
 * conversations -- EmbedPartner.tsx and EmbedConversations.tsx produce
 * byte-identical markup and differ only in which endpoint they call -- and it
 * could only ever name one partner, which is exactly the limitation this page
 * now exists to remove. It is a multi-select filter below, applied to both
 * content types.
 *
 * /embed/partners/:slug itself is untouched and still served. A live Out of
 * Hand Theater page frames it today.
 */
const CONTENT_TYPES: ReadonlyArray<{ value: ContentType; label: string }> = [
  { value: 'conversations', label: 'Conversations' },
  { value: 'topics', label: 'Topics' },
]

const MAX_ITEMS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Unlimited' },
  { value: '3', label: '3' },
  { value: '6', label: '6' },
  { value: '9', label: '9' },
  { value: '12', label: '12' },
]

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    section: { marginBottom: theme.spacing(3) },
    codeBlock: {
      background: '#0f172a', borderRadius: 6, padding: theme.spacing(2),
      fontFamily: 'monospace', fontSize: 12, color: '#a5f3fc',
      lineHeight: 1.6, overflowX: 'auto' as const,
      whiteSpace: 'pre-wrap' as const, wordBreak: 'break-all' as const,
    },
    preview: { border: '2px dashed #cbd5e1', borderRadius: 8, overflow: 'hidden', background: '#f1f5f9' },
    previewFrame: { display: 'block', border: 0, width: '100%' },
    copyButton: { marginTop: theme.spacing(1) },
    colorSwatch: {
      width: 24, height: 24, borderRadius: 4, border: '1px solid #d1d5db',
      display: 'inline-block', marginRight: theme.spacing(1), verticalAlign: 'middle',
    },
  })
)

export interface BuildEmbedUrlOptions {
  contentType: ContentType
  partnerIds?: number[]
  embedTheme?: EmbedTheme
  accent?: string
  showHeader?: boolean
  headerText?: string
  maxItems?: string
}

/**
 * The embed URL for a given configuration.
 *
 * An options object rather than the seven positional arguments this used to
 * take -- `partner_ids` would have been an eighth, and the call site was
 * already unreadable at seven.
 *
 * Every param is omitted at its default value, and that omission is the
 * contract: a default configuration must produce exactly `/embed/conversations`
 * with no query string at all, so the snippet a customer pastes has nothing in
 * it that does not need to be there. The defaults below mirror
 * useEmbedParams()'s own fallbacks in EmbedLayout.tsx; if one side changes, the
 * generator starts emitting params that are already the default.
 *
 * Exported for the unit test -- the assertions live in
 * test/components/admin/v2/embedUrl.test.ts.
 */
export function buildEmbedUrl({
  contentType,
  partnerIds = [],
  embedTheme = 'light',
  accent = '#eab308',
  showHeader = false,
  headerText = '',
  maxItems = ''
}: BuildEmbedUrlOptions): string {
  const base = window.location.origin
  const path = `/embed/${contentType}`

  const params = new URLSearchParams()
  if (embedTheme !== 'light') params.set('theme', embedTheme)
  if (accent !== '#eab308') params.set('accent', accent)
  if (showHeader && headerText) params.set('header', headerText)
  if (maxItems) params.set('maxItems', maxItems)
  // Comma-joined, matching what api/embed/ConversationsApi.ts sends. The
  // repeat-key form the API also tolerates would work, but then the URL in the
  // customer's snippet would not match the URL the embed requests.
  if (partnerIds.length > 0) params.set('partner_ids', partnerIds.join(','))

  const qs = params.toString()
  return `${base}${path}${qs ? `?${qs}` : ''}`
}

function buildIframeCode(url: string, height: string): string {
  return `<iframe\n  src="${url}"\n  width="100%"\n  height="${height}"\n  frameborder="0"\n  style="border:0"\n></iframe>`
}

function buildScriptCode(url: string, height: string): string {
  return [
    '<div id="equitable-dinners-embed"></div>',
    '<script>',
    '(function() {',
    "  var iframe = document.createElement('iframe');",
    `  iframe.src = '${url}';`,
    "  iframe.width = '100%';",
    `  iframe.height = '${height}';`,
    "  iframe.frameBorder = '0';",
    "  iframe.style.border = '0';",
    "  document.getElementById('equitable-dinners-embed').appendChild(iframe);",
    '})();',
    '</script>',
  ].join('\n')
}

/**
 * The preview iframe reloads on every change to its src, so debounce the URL --
 * otherwise typing in "Header text" reloads the embed once per keystroke.
 */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return debounced
}

const Embeds: React.FC = () => {
  const classes = useStyles()
  const [contentType, setContentType] = useState<ContentType>('conversations')
  // Local state, not MST. This page has no store wiring and needs none -- the
  // selection is scratch input for a snippet, not application state.
  const [selectedPartners, setSelectedPartners] = useState<PartnerOption[]>([])
  const [height, setHeight] = useState('600')
  const [maxItems, setMaxItems] = useState('')
  const [embedTheme, setEmbedTheme] = useState<EmbedTheme>('light')
  const [accent, setAccent] = useState('#eab308')
  const [showHeader, setShowHeader] = useState(true)
  const [headerText, setHeaderText] = useState('Upcoming Conversations')
  const [codeFormat, setCodeFormat] = useState<CodeFormat>('iframe')
  const [copied, setCopied] = useState(false)

  // `selectedPartners` is a new array on every selection change, so the joined
  // id list is the dep -- an array identity here would rebuild embedUrl on every
  // render, and embedUrl feeds a debounced iframe src that reloads when it does.
  const partnerIdsKey = selectedPartners.map((partner) => partner.id).join(',')

  const embedUrl = useMemo(
    () =>
      buildEmbedUrl({
        contentType,
        partnerIds: partnerIdsKey ? partnerIdsKey.split(',').map(Number) : [],
        embedTheme,
        accent,
        showHeader,
        headerText,
        maxItems
      }),
    [contentType, partnerIdsKey, embedTheme, accent, showHeader, headerText, maxItems]
  )

  const previewUrl = useDebounced(embedUrl, 400)

  const embedCode = codeFormat === 'iframe'
    ? buildIframeCode(embedUrl, height)
    : buildScriptCode(embedUrl, height)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API unavailable (HTTP or permissions denied)
    }
  }

  return (
    <div>
      <Typography variant="h5" gutterBottom>Embeds</Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Generate embed codes to display conversations or topics on external websites, optionally scoped to
        one or more partners.
      </Typography>

      <Card className={classes.section}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Preview Pages</Typography>
          <Grid container spacing={2}>
            <Grid item>
              <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} href="/embed/conversations" target="_blank">
                Conversations
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} href="/embed/topics" target="_blank">
                Topics
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} href={embedUrl} target="_blank">
                Current Config
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card className={classes.section}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Configure</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Content Type</InputLabel>
                <Select value={contentType} onChange={(e) => setContentType(e.target.value as ContentType)} label="Content Type">
                  {CONTENT_TYPES.map((ct) => <MenuItem key={ct.value} value={ct.value}>{ct.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {/*
              Shown for both content types, and a picker rather than the free-text
              slug field it replaces. The old field could not be verified -- a
              typo produced a URL that looked fine and rendered an empty embed --
              and it could name only one partner, which is why Out of Hand
              Theater (partners 68 and 621) could not have a working embed at all.
            */}
            <Grid item xs={12} sm={4}>
              <PartnerMultiSelect
                id="ic-admin-embeds-partners"
                value={selectedPartners}
                onChange={setSelectedPartners}
                label="Partners (optional)"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth variant="outlined" size="small" label="Height (px)" type="number" value={height} onChange={(e) => setHeight(e.target.value)} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Max Items</InputLabel>
                <Select value={maxItems} onChange={(e) => setMaxItems(e.target.value as string)} label="Max Items">
                  {MAX_ITEMS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Theme</InputLabel>
                <Select value={embedTheme} onChange={(e) => setEmbedTheme(e.target.value as EmbedTheme)} label="Theme">
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="dark">Dark</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth variant="outlined" size="small" label="Accent Color" value={accent} onChange={(e) => setAccent(e.target.value)}
                InputProps={{ startAdornment: <span className={classes.colorSwatch} style={{ background: accent }} /> }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={showHeader} onChange={(e) => setShowHeader(e.target.checked)} color="primary" />}
                label="Show header"
              />
              {showHeader && (
                <TextField variant="outlined" size="small" label="Header text" value={headerText} onChange={(e) => setHeaderText(e.target.value)} style={{ marginLeft: 16, minWidth: 300 }} />
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card className={classes.section}>
        <CardContent>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Typography variant="h6">Preview</Typography>
            <Typography variant="caption" color="textSecondary">{height} x 100% px</Typography>
          </div>
          {/*
            A real iframe on the real embed URL, not an in-process render of the
            same components. The in-process version could not fail the way the
            deployed embed fails: it looked perfect for the whole period the app
            was answering /embed/* with X-Frame-Options: DENY and every embed on
            the internet was blank. If this preview is blank, the embed is blank.
          */}
          <div className={classes.preview} style={{ height: Number(height) }}>
            <iframe
              src={previewUrl}
              title="Embed preview"
              height={height}
              className={classes.previewFrame}
            />
          </div>
          <Typography variant="caption" color="textSecondary">
            Loaded in an iframe from {previewUrl}. A blank preview means the embed is being blocked from
            framing — check that the response carries a frame-ancestors policy and no X-Frame-Options.
          </Typography>
        </CardContent>
      </Card>

      <Card className={classes.section}>
        <CardContent>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Typography variant="h6">Embed Code</Typography>
            <div>
              <Button variant={codeFormat === 'iframe' ? 'contained' : 'outlined'} size="small" onClick={() => setCodeFormat('iframe')} style={{ borderRadius: '4px 0 0 4px' }}>iframe</Button>
              <Button variant={codeFormat === 'script' ? 'contained' : 'outlined'} size="small" onClick={() => setCodeFormat('script')} style={{ borderRadius: '0 4px 4px 0' }}>script</Button>
            </div>
          </div>
          <div className={classes.codeBlock}>{embedCode}</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button className={classes.copyButton} variant="contained" color="primary" startIcon={<FileCopyIcon />} onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default Embeds
